// PostHog client-side initialization for the Devotion Fund SPA (Vite + React).
//
// Capture-by-default, cookieless-by-default (portfolio analytics standard):
// every visitor is counted anonymously with NOTHING written to their device
// (persistence:"memory" + disable_persistence). "Accept" in the consent banner
// upgrades to the persistent tier (cross-session identity + session replay);
// "Decline" opts out of all analytics.
//
// Timing: init runs at MODULE LOAD (called from src/main.tsx before render),
// not inside a useEffect — posthog-js silently drops any capture made before
// .init() completes, and React runs child effects before parent effects, so a
// $pageview could otherwise fire first.
//
// PRERENDER GUARD (critical): this repo prerenders marketing routes with a
// headless browser (scripts/prerender.mjs, puppeteer). That pass sets
// window.__PRERENDER_INJECTED = true (via evaluateOnNewDocument) BEFORE any app
// JS runs. We hard-skip init there so no analytics fire during prerender and no
// PostHog state is baked into the static HTML that ships to every visitor.

import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

// Public project token — safe to ship in the client bundle. Kept as a built-in
// fallback so the production build never resolves to an empty token even if the
// env var is missing; VITE_POSTHOG_KEY overrides it when set. PostHog project 77713.
const POSTHOG_TOKEN_FALLBACK = 'phc_6clLdmOuuccfeYCtZ7XmEK4vTqjQ8lBAXnkAawZ05zi';

export const CONSENT_COOKIE = 'devotionfund_consent';

function readConsentCookie(): 'granted' | 'denied' | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]+)`)
  );
  const v = m ? decodeURIComponent(m[1]) : null;
  return v === 'granted' || v === 'denied' ? v : null;
}

/** True during the headless prerender pass — never touch analytics then. */
function isPrerender(): boolean {
  return (
    typeof window === 'undefined' ||
    (window as unknown as { __PRERENDER_INJECTED?: boolean })
      .__PRERENDER_INJECTED === true
  );
}

/**
 * True for automated browsers (Playwright/Selenium/Puppeteer via CDP), which
 * set navigator.webdriver. Catches our own production Playwright smoke test
 * and manual "load the live site to verify" checks so they don't inflate
 * visitor counts.
 */
function isWebdriver(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator as unknown as { webdriver?: boolean }).webdriver === true
  );
}

/**
 * Confirmed fabricated traffic, not a real device: the exact user-agent string
 * "moto g power (2022)" + "Chrome/151.0.0.0" has shown up repeatedly from the
 * same Hetzner IPv6 address (2a01:4f8:c2c:ffcc::1) across at least 7 portfolio
 * products, each time in a tight multi-event burst -- a real Motorola handset
 * doesn't share an IP with unrelated SaaS products' health checks. On this
 * product alone it accounted for 18 of 33 $pageview events in a 30-day window
 * (confirmed via PostHog HogQL, 2026-09-08) -- over a third of "visitors" on
 * an already-thin-traffic product. navigator.webdriver is false for it (same
 * as the /render crawler above), so isWebdriver() doesn't catch it either.
 */
function isKnownFabricatedDevice(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('moto g power (2022)') &&
    navigator.userAgent.includes('Chrome/151.0.0.0')
  );
}

/**
 * '/render' isn't a route this app defines (App.tsx has no such <Route>, it
 * only ever reaches the SPA's not-found fallback) -- yet PostHog shows real
 * pageviews there every few days, from a different Chrome build each time
 * (149/150/151, i.e. an auto-updating headless browser, not a person) and
 * confirmed on at least one other portfolio product's PostHog project too
 * (same path, same cadence). That matches an external render/screenshot
 * tool -- likely one of our own or the Control Center's -- executing real JS
 * against "<our-domain>/render" as a health-check or preview step. It never
 * sets navigator.webdriver (new headless Chrome doesn't), so isWebdriver()
 * above doesn't catch it. Since no legitimate visitor can ever land here on
 * purpose, excluding this exact path from analytics is safe.
 */
function isKnownCrawlerPath(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === '/render';
}

/**
 * True when the app is loaded against a local dev server (Vite on localhost,
 * 127.0.0.1, or a *.localhost alias), never a real visitor. Measured via
 * HogQL 2026-10-11: 317 of 1205 $pageview events in a 30-day window (26%)
 * came from a devotionfund.localhost / 127.0.0.1 origin -- agent/QA sessions
 * running `npm run dev` and loading the page, silently inflating every
 * traffic-derived decision (visitor counts, CTA click rates, micro-survey
 * dismissal rates) on an already-thin-traffic product.
 */
function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}

/**
 * The portfolio's own Control Center uptime prober drives a real headless
 * browser against production with navigator.webdriver suppressed ("stealth"
 * mode meant to defeat bot detection), which defeats isWebdriver() above. It
 * still names itself in its UA string ("... ConceptZoneProbe/1"). Ported
 * from unifind (5cff866), then swept via mystudyfund (f7233d9) -- which fully
 * excluded this UA from capture, which ALSO silenced the same prober's own
 * "analytics-probe-<product>" uptime heartbeat (the Control Center alarms if
 * PostHog never sees that visit). mystudyfund fixed this at the source
 * (MSF-224, commit eb9522b): tag the probe in before_send() instead of
 * opting it out at init. Ported here the same way.
 */
function isKnownProbe(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('ConceptZoneProbe')
  );
}

/**
 * Initialise PostHog once, at module load. No-op during prerender, when no
 * token is available, or if already initialised (hot-reload safe).
 */
export function initPostHog(): void {
  if (
    isPrerender() ||
    isWebdriver() ||
    isKnownCrawlerPath() ||
    isKnownFabricatedDevice() ||
    isLocalDevHost()
  )
    return;

  const key =
    (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ??
    POSTHOG_TOKEN_FALLBACK;
  if (!key) return;

  // posthog-js sets __loaded after a successful init; guard against re-init.
  if ((posthog as unknown as { __loaded?: boolean }).__loaded) return;

  // Prior consent decision, if any.
  const consent = readConsentCookie();

  posthog.init(key, {
    api_host:
      (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ??
      'https://eu.i.posthog.com',
    ui_host: 'https://eu.posthog.com',
    // Tag (not drop) the Control Center uptime prober -- see isKnownProbe()
    // above for why this must stay in capture rather than be excluded.
    before_send: (cr: CaptureResult | null): CaptureResult | null => {
      if (cr && isKnownProbe() && cr.properties) {
        cr.properties.$is_known_probe = true;
      }
      return cr;
    },
    // Override the "identified_only" default: without this, anonymous
    // pageviews from marketing visitors are silently dropped by capture()'s
    // person-processing gate. "always" makes the gate pass so basic reach
    // (pageviews) actually records.
    person_profiles: 'always',
    // ── TTDSG §25 + GDPR, two-tier by design ──────────────────────────────
    //  DEFAULT (no consent decision yet) — BASIC, COOKIELESS, ANONYMOUS:
    //    persistence:"memory" + disable_persistence:true writes NOTHING to the
    //    device (ephemeral in-memory distinct_id per pageview), so §25 TTDSG is
    //    not triggered and no consent is required. Lawful under GDPR Art.6(1)(f)
    //    legitimate interest, minimised: EU-hosted, no cross-session identifier,
    //    session recording OFF. We therefore capture by DEFAULT.
    //  ON "Accept" (consent-banner) — persistence → "localStorage+cookie" +
    //    session recording on. THAT device storage is what §25 consent covers.
    //  ON "Decline" — opt out of everything (handled just below).
    // No `respect_dnt`: browser Do-Not-Track has no legal force in the EU and
    // would silently blind analytics even for visitors who clicked "Accept".
    persistence: consent === 'granted' ? 'localStorage+cookie' : 'memory',
    disable_persistence: consent !== 'granted',
    disable_session_recording: consent !== 'granted',
    // This app has no posthog.getFeatureFlag/isFeatureEnabled call anywhere
    // (grepped src/ — zero hits), yet posthog-js fetches /flags/ on init by
    // default regardless of consent. opt_out_capturing() below stops event
    // capture but NOT that request, so a "Decline" visitor still had a flags
    // POST fired at init time — a real leak (sportfund's TSF-23), applicable
    // here since nothing in this product reads a flag back anyway.
    advanced_disable_feature_flags: true,
    // Until this shipped, an uncaught client error (or a silently-swallowed
    // one a call site chooses to report via posthog.captureException, see
    // MyPage.tsx's handleCoverImageChange) left zero trace anywhere we can
    // query. Confirmed the gap is real: a real signup (35LDqWd6..., "Christa")
    // reached /creator and
    // /mission/christa-hi with ~40 recorded interactions on 2026-09-21, yet
    // missionpages/35LDqWd6... was never created (paths/christa-hi exists,
    // the missionpages doc 404s) — there was no way to see why. Needs only
    // the existing public project token, not the admin error-tracking scope
    // that CZ-730 was declined for (that's for reading PostHog's Error
    // Tracking product from our own scripts; this is the SDK's own capture).
    autocaptureExceptions: true,
  });

  if (consent === 'denied') {
    // Honour an explicit opt-out: stop all capture + wipe any stale storage a
    // previous (pre-decision) visit may have left on the device.
    posthog.opt_out_capturing();
    posthog.reset(true);
  }

  // Deliberately NOT exposed on window.posthog. GTM-PR2P768F (the rogue tag
  // tracked by DF-95) fires a classic PostHog snippet on this same page; that
  // kind of snippet checks window.<lib> for a stub loader (a private flag like
  // __SV that only the STUB sets) before deciding whether to init its own
  // client. Handing it a reference to our real, already-initialized client
  // tricks it into treating our client as its own stub queue, and its own
  // init(key) call can then reconfigure/pollute the client we actually rely
  // on for analytics. Deleting the rogue GTM tag itself still needs a human
  // (DF-95); this just stops our own code from making that tag's interference
  // possible.
}

export { posthog };
